// @flow
import { merge, omit } from "timm";

import { actions as apiActions } from "../../api";
import apiRequest from "../../api/request";
import { getJsonApiClient, setEntityModel } from "../../api/apiClient";
// $FlowIgnore TS import
import { constructApiUrl, sendRequest } from "../../api/useFetch";
import type { PendingOffer as PendingOffersType } from "../../../types/PendingOffers";
// $FlowIgnore TS import
import type { GetState } from "../../../types/ReduxType";
// $FlowIgnore TS import
import type { Action, ActionsObject } from "../../../types/Action";
import type { Entity } from "../../../types/Entity";

// Constants
export const ENTITY: Entity = "pending_offers";

// Actions
export const actions = {
  ITEM_SUCCESS: apiActions.getItemSuccessActionName(ENTITY),
  REMOVE: apiActions.getFailureActionName(ENTITY, "record_not_found"),
  FAILURE: apiActions.getFailureActionName(ENTITY),
  REQUEST: apiActions.getRequestActionName(ENTITY),
};

type State = {
  error: boolean,
  isFetching: boolean,
  state: ?$PropertyType<PendingOffersType, "state">,
  token: $PropertyType<PendingOffersType, "token">,
};
// InitialState
export const initialState: State = {
  isFetching: false,
  error: false,
  token: "",
  state: "reject",
};

// Reducer
export default function reducer(
  state: typeof initialState = initialState,
  { payload, type, meta }: Action,
) {
  switch (type) {
    case actions.REQUEST:
      return merge(state, {
        isFetching: true,
        state: meta.state,
      });
    case actions.REMOVE:
    case actions.FAILURE:
      return merge(state, payload, {
        error: true,
        isFetching: false,
      });
    case actions.ITEM_SUCCESS:
      return merge(state, payload, {
        error: false,
        isFetching: false,
      });
    default:
      return state;
  }
}

// Action creators
export const actionCreators = {
  setPendingOffer(
    token: $PropertyType<PendingOffersType, "token">,
    state: $PropertyType<PendingOffersType, "state">,
  ) {
    return function (dispatch: Dispatch) {
      return apiRequest({
        apiAction: "patch",
        apiModel: {
          state,
          id: token,
        },
        apiUrl: getJsonApiClient().one(ENTITY, token),
        dispatch,
        entityName: ENTITY,
        id: token,
        requestPayload: {
          state,
        },
      });
    };
  },
  // Used when the email deep-link carries a pending_offer_uuid (a
  // fulfillment-owned offer, CHAU-1195): the request goes to the marketplace
  // API (POST /api/v1/chauffeur/pending-offers/{uuid}/acceptance|rejection),
  // authorized by the logged-in user's identity headers. The email token is
  // deliberately not sent — the uuid identifies the offer and marketplace
  // verifies it belongs to the caller's LSP.
  setMarketplacePendingOffer(
    uuid: string,
    state: $PropertyType<PendingOffersType, "state">,
  ) {
    return function (dispatch: Dispatch, getState: GetState) {
      dispatch({
        type: actions.REQUEST,
        meta: { state },
        payload: {},
      });

      if (!uuid) {
        dispatch({
          type: actions.FAILURE,
          payload: { code: "save_failed" },
        });
        return Promise.resolve();
      }

      const me = getState().me || {};
      const roles = (me.roles || []).filter((role) => role !== "on_demand");
      const action = state === "accept" ? "acceptance" : "rejection";
      const url = constructApiUrl(
        `/api/v1/chauffeur/pending-offers/${uuid}/${action}`,
      );

      return sendRequest(url, {
        method: "POST",
        // Same identity-header workaround as marketplaceService.ts: the
        // gateway authorizer does not populate lsp/bd context, so the SPA
        // supplies them from /me.
        headers: {
          "x-user-id": me.id || "",
          "x-user-roles": roles.join(","),
          "x-user-lsp-id": (me.lsp && me.lsp.id) || "",
          "x-user-bd-id":
            (me.lsp &&
              me.lsp.businessDistrict &&
              me.lsp.businessDistrict.uuid) ||
            "",
        },
      })
        .then(() => {
          dispatch({ type: actions.ITEM_SUCCESS, payload: {} });
        })
        .catch((error) => {
          // Marketplace errors carry {code, message, detail}. A 409 means the
          // offer is already accepted, rejected, or expired — surface its
          // detail; a 404 means unknown offer (or another LSP's), which has a
          // dedicated translation.
          if (error && error.status === 404) {
            dispatch({
              type: actions.FAILURE,
              payload: { code: "record_not_found" },
            });
            return;
          }

          const detail =
            error && error.status === 409 && error.body
              ? error.body.detail
              : null;
          dispatch({
            type: actions.FAILURE,
            payload: { code: "save_failed", detail: detail || null },
          });
        });
    };
  },
};

// Side effects
setEntityModel(ENTITY, initialState, {
  serializer: (resourceAttributes) => {
    return {
      attributes: omit(resourceAttributes, "id"),
      id: resourceAttributes.id,
      type: ENTITY,
    };
  },
});
